import { useBoqVersionHistory } from "./useBillOfQuantities";
import {
  getBoqVersionPrefixFromValue,
  getNumericBoqVersion,
  getScopedLatestVersionNumber,
} from "../utils/boqVersionPrefix";

// Single source of truth for "what's this job type's opportunity's current
// version status" - used by both Header.tsx's navbar Popover and
// StepperNavigation.tsx's Calculate & Review skip-save check, so the two
// never diverge. One shared fetch: both job types' versions come back
// together for a project.
export const useJobTypeVersionStatus = (
  projectName: string | undefined,
  opportunityNo: string | undefined,
  hasGroundworksOpportunity: boolean,
  hasRoadsAndSewersOpportunity: boolean,
) => {
  const { data, isLoading } = useBoqVersionHistory(projectName, {
    oppNo: opportunityNo,
    enabled: hasGroundworksOpportunity || hasRoadsAndSewersOpportunity,
  });

  const getStatus = (prefix: "GWKS" | "RS"): string | undefined => {
    const versions = data?.versions;
    if (!versions) return undefined;
    const latestNum = getScopedLatestVersionNumber(versions, prefix);
    if (latestNum === undefined) return undefined;
    return versions.find(
      (v) =>
        getBoqVersionPrefixFromValue(v.version) === prefix &&
        getNumericBoqVersion(v.version) === latestNum,
    )?.snapshot?.status;
  };

  return {
    groundworksVersionStatus: getStatus("GWKS"),
    roadsAndSewersVersionStatus: getStatus("RS"),
    isLoading,
  };
};

// Status casing is inconsistent across the app - both "In-progress" and
// "In-Progress" get written as real values (TenderDetails.tsx's
// completePayload, JobTypeSelection.tsx, VersionHistory.tsx's reactivate
// flow all use capital-P; TenderContext's default/most form saves use
// lowercase-p) - so this must compare case-insensitively, never with a
// strict `=== "In-progress"`. `undefined` (status not loaded yet, or no
// version exists yet e.g. a brand-new tender) counts as in progress too -
// there's nothing to indicate otherwise, so callers should still save.
export const isVersionStatusInProgress = (status: string | undefined): boolean =>
  !status || status.trim().toLowerCase() === "in-progress";
